import { useState as _useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../hooks/use-toast';
import { componentLogger } from '@/lib/logging';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Form } from '../../../components/ui/form';
import {
  UsersActionDialogProps,
  UserFormData,
  AssignmentPermission,
  createUserFormSchemaFactory,
  editUserFormSchemaFactory,
} from './shared/UserDialogTypes';
import {
  useUserDialogPermissions,
  useCompaniesData,
  useAssignmentPermissions,
  getDefaultLanguage,
} from './shared/UserDialogShared';
import { ProfileFieldsSection } from './shared/ProfileFieldsSection';
import { CompanyRoleSection } from './shared/CompanyRoleSection';
import { AssignmentPermissionsSection } from './shared/AssignmentPermissionsSection';

// Import extracted hooks
import { useUserFormSubmission } from './shared/hooks/useUserFormSubmission';
import { usePermissionSync } from './shared/hooks/usePermissionSync';

export function UsersActionDialog({
  userToEdit,
  open,
  onOpenChange,
  onUserMutated,
  mode,
}: UsersActionDialogProps) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Use our custom hooks for permissions and data
  const {
    userContext,
    isInvokerCompanyAdmin,
    invokerCompanyId,
    permissions,
  } = useUserDialogPermissions(userToEdit, mode);

  const { companies, companiesLoading } = useCompaniesData(userContext, isInvokerCompanyAdmin);

  const {
    assignments,
    assignmentsLoading,
    assignmentPermissions,
    permissionsLoading,
    originalPermissions,
    addAssignmentPermission,
    removeAssignmentPermission,
    updateAssignmentPermissionLevel,
    updateEmailNotificationPreference,
  } = useAssignmentPermissions(userToEdit, userContext, isInvokerCompanyAdmin, invokerCompanyId);

  // Permission sync hook
  const {
    syncingPermissions,
    syncProgress,
    setSyncingPermissions: _setSyncingPermissions,
    setSyncProgress: _setSyncProgress,
    syncAssignmentPermissions,
    syncEmailPreferences: syncEmailPrefs,
  } = usePermissionSync();

  // Email preferences are now included in assignmentPermissions from the edge function
  const emailPreferencesLoading = false; // No longer needed as email prefs are loaded with permissions

  // Wrapper function to match expected signature for translation
  const tWrapper = useCallback((_key: string, _options?: string | Record<string, unknown> | undefined): string => {
    const fallback = typeof _options === 'string' ? _options : undefined;
    return t(_key, fallback || _key);
  }, [t]);

  // Form setup with appropriate schema
  const formSchema = useMemo(() => {
    return isEdit
      ? editUserFormSchemaFactory(tWrapper)
      : createUserFormSchemaFactory(tWrapper);
  }, [isEdit, tWrapper]);

  const defaultValues = useMemo(() => {
    const baseDefaults = {
      firstName: userToEdit?.first_name || '',
      lastName: userToEdit?.last_name || '',
      phoneNumber: userToEdit?.phone_number || '',
      language: getDefaultLanguage(userToEdit, userContext),
      app_role: userToEdit?.app_role || 'USER',
      companyId: userToEdit?.company_id || (isInvokerCompanyAdmin ? invokerCompanyId : undefined),
      isRepresentative: userToEdit?.is_representative || false,
      status: userToEdit?.status || 'ACTIVE',
      title: userToEdit?.title || '',
      // assignmentPermissions removed - they are loaded separately via useAssignmentPermissions
    };

    // Add email field for create mode
    if (!isEdit) {
      return {
        ...baseDefaults,
        email: '',
      };
    }

    return baseDefaults;
  }, [userToEdit, userContext, isEdit, isInvokerCompanyAdmin, invokerCompanyId]);

  const form = useForm<UserFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onChange',
  });

  // Enhanced email preferences sync wrapper
  const syncEmailPreferences = async (
    userId: string,
    assignmentPermissions: AssignmentPermission[],
    forceUpdate = false
  ) => {
    // Convert originalPermissions to email preferences format for compatibility
    const originalEmailPreferences: Record<string, boolean> = {};
    originalPermissions.forEach(perm => {
      originalEmailPreferences[perm.assignmentId] = perm.emailNotifications;
    });
    return syncEmailPrefs(userId, assignmentPermissions, originalEmailPreferences, forceUpdate);
  };

  // Form submission hook
  const {
    isSubmittingForm,
    onSubmit,
    duplicateEmailError,
    clearDuplicateEmailError
  } = useUserFormSubmission({
    form,
    mode,
    userContext,
    isInvokerCompanyAdmin,
    canEditProfileFields: permissions.canEditProfileFields,
    onOpenChange,
    syncAssignmentPermissions,
    syncEmailPreferences,
    originalPermissions,
    ...(userToEdit !== undefined && { userToEdit }),
    ...(invokerCompanyId !== undefined && { invokerCompanyId }),
    ...(onUserMutated !== undefined && { onUserMutated }),
  });

  // Scroll to top when duplicate email error is shown
  useEffect(() => {
    if (duplicateEmailError && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [duplicateEmailError]);

  // Sync assignment permissions to form when they change
  useEffect(() => {
    componentLogger.debug('users-action-dialog', 'Setting assignmentPermissions to form', {
      permissionsCount: assignmentPermissions.length,
      permissions: assignmentPermissions,
      isEdit,
      open
    });
    form.setValue('assignmentPermissions', assignmentPermissions);
  }, [assignmentPermissions, form, isEdit, open]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && !isEdit) {
      // Only reset for create mode to avoid clearing loaded permissions in edit mode
      // BUT preserve assignmentPermissions to avoid race condition
      const currentPermissions = form.getValues('assignmentPermissions');
      componentLogger.debug('users-action-dialog', 'Form reset in create mode - preserving permissions', {
        permissionsBeforeReset: currentPermissions?.length || 0,
        permissions: currentPermissions
      });
      form.reset(defaultValues);
      // Restore permissions after reset to avoid race condition
      if (currentPermissions && currentPermissions.length > 0) {
        componentLogger.debug('users-action-dialog', 'Restoring permissions after form reset', {
          permissionsCount: currentPermissions.length
        });
        form.setValue('assignmentPermissions', currentPermissions);
      }
    } else if (open && isEdit) {
      // For edit mode, populate form with user data but don't include assignmentPermissions
      // assignmentPermissions are managed separately via useAssignmentPermissions hook
      Object.keys(defaultValues).forEach(key => {
        if (key !== 'assignmentPermissions') {
          form.setValue(key as keyof typeof defaultValues, defaultValues[key as keyof typeof defaultValues]);
        }
      });
    } else if (!open && !isSubmittingForm) {
      // Always reset when closing (to clean up for next use)
      form.reset(defaultValues);
    }
  }, [open, form, defaultValues, isSubmittingForm, isEdit]);

  // Enhanced permission handlers that work with form
  const handleAddAssignmentPermission = (assignmentId: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return;

    const existing = assignmentPermissions.find(p => p.assignmentId === assignmentId);
    if (existing) {
      toast({
        title: t('usersActionDialog.toast.assignmentAlreadyAssignedTitle'),
        description: t('usersActionDialog.toast.assignmentAlreadyAssignedDescription'),
        variant: 'destructive',
      });
      return;
    }

    addAssignmentPermission(assignmentId);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        if (!state && !isSubmittingForm) {
          form.reset(defaultValues);
        }
        onOpenChange(state);
      }}
    >
      <DialogContent className="sm:max-w-[550px] h-[85vh] max-h-[800px] min-h-[700px] overflow-hidden flex flex-col" data-testid="user-modal">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('usersActionDialog.titleEdit')
              : t('usersActionDialog.titleCreate')}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('usersActionDialog.descriptionEdit')
              : t('usersActionDialog.descriptionCreate')}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto pr-6 space-y-4"
          style={{ maxHeight: 'calc(85vh - 180px)' }}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-1" data-testid="user-form">

              {/* Profile Fields Section */}
              <ProfileFieldsSection
                form={form}
                isSubmittingForm={isSubmittingForm}
                isEdit={isEdit}
                mode={mode}
                t={tWrapper}
                duplicateEmailError={duplicateEmailError}
                onClearDuplicateEmailError={clearDuplicateEmailError}
                userToEdit={userToEdit}
              />

              {/* Company and Role Section */}
              <CompanyRoleSection
                form={form}
                isSubmittingForm={isSubmittingForm}
                isEdit={isEdit}
                companies={companies}
                companiesLoading={companiesLoading}
                userContext={userContext}
                canEditProfileFields={permissions.canEditProfileFields}
                shouldShowStatusField={permissions.canEditUserStatus}
                shouldMakeRoleEditable={permissions.canEditUserRole}
                t={tWrapper}
                {...(userToEdit !== undefined && { userToEdit })}
              />

              {/* Assignment Permissions Section */}
              <AssignmentPermissionsSection
                form={form}
                isSubmittingForm={isSubmittingForm}
                isEdit={isEdit}
                assignments={assignments}
                assignmentsLoading={assignmentsLoading}
                assignmentPermissions={assignmentPermissions}
                permissionsLoading={permissionsLoading}
                emailPreferencesLoading={emailPreferencesLoading}
                syncingPermissions={syncingPermissions}
                syncProgress={syncProgress}
                canEditAssignmentPermissions={permissions.canEditAssignmentPermissions}
                canEditEmailNotifications={permissions.canEditEmailNotifications}
                addAssignmentPermission={handleAddAssignmentPermission}
                removeAssignmentPermission={removeAssignmentPermission}
                updateAssignmentPermissionLevel={updateAssignmentPermissionLevel}
                updateEmailNotificationPreference={updateEmailNotificationPreference}
                t={tWrapper}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmittingForm || syncingPermissions}
                  data-testid="cancel-button"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmittingForm || syncingPermissions}
                  data-testid="save-user-button"
                >
                  {isSubmittingForm
                    ? (isEdit ? t('common.saving') : t('common.creating'))
                    : (isEdit ? t('usersActionDialog.buttons.saveChanges') : t('usersActionDialog.buttons.createUser'))}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}